import { z } from "zod";
import { URLSchema } from "./common";
import type { UiAvatarUploadItem } from "@shopexperts/shared/dist/types/ui/UiAvatarUpload.vue";
import { AgencyTypeSchema, ShopifyPartnerTierSchema, type AgencyTypeFields } from "@shopexperts/shared";

// Base schema with common fields for both freelancers and agencies
const baseExpertSchema = z.object({
    startingPrice: z.coerce.number().optional(),
    country: z.string(),
    cityId: z.string().regex(/^\d*$/).optional(),
    businessAddress: z.string(),
    shortBio: z.string().optional(),
    website: URLSchema.optional(),
    phone: z.string().refine((val) => {
        if (!val || val === '') return true;
        const phoneRegex = /^\+[1-9]\d{1,14}$/;
        return phoneRegex.test(val);
    }, {
        message: "Phone number must include country code (e.g., +1234567890)"
    }),
    timezone: z.string(),
    linkedin: URLSchema.optional(),
    github: URLSchema.optional(),
    x: URLSchema.optional(),
    fiverr: URLSchema.optional(),
    upwork: URLSchema.optional(),
    toptal: URLSchema.optional(),
    storetasker: URLSchema.optional(),
    otherListing: URLSchema.optional(),
    geoRegions: z.array(z.string()).optional(),
    languages: z.array(z.string()).optional(),
    subcategories: z.array(z.string()).optional(),
    images: z.array(z.any()).max(3).optional(),
    avatar: z.custom<UiAvatarUploadItem>().optional(),
    badgeIds: z.array(z.number()).optional(),
    partnerTier: ShopifyPartnerTierSchema.nullable().optional(),
    appTier: z.boolean().optional(),
    shopifyDirectoryLink: URLSchema.optional(),
    bookingCallUrl: URLSchema.optional(),
  googleMerchant: URLSchema.optional(),
});

// Freelancer-specific schema
export const freelancerDetailsSchema = baseExpertSchema.extend({
    expertType: z.literal('freelancer'),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    role: z.string().optional(),
});

// Agency-specific schema
export const agencyDetailsSchema = baseExpertSchema.extend({
    expertType: z.literal('agency'),
    agencyName: z.string().min(1, 'Agency name is required'),
    agencyType: AgencyTypeSchema.optional(),
    agencyTypeFields: z.custom<AgencyTypeFields>().optional(),
    representativePicture: z.custom<UiAvatarUploadItem>().optional(),
    representativeName: z.string().optional(),
    representativeRole: z.string().optional(),
});

// Union schema for the form
export const detailsFormSchema = z.discriminatedUnion('expertType', [
    freelancerDetailsSchema,
    agencyDetailsSchema,
]);

export type FreelancerDetailsSchema = z.infer<typeof freelancerDetailsSchema>;
export type AgencyDetailsSchema = z.infer<typeof agencyDetailsSchema>;
export type DetailsFormSchema = z.infer<typeof detailsFormSchema>;
